import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart2, RefreshCw, Send, Download,
  Loader2, FileText, CheckCircle, Clock,
  Sparkles, X
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import DaysInsightsReport from '@/components/DaysInsightsReport';
import MonthlyInsightExtras from '@/components/MonthlyInsightExtras';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { renderTextReportPdf } from '@/lib/textReportPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const moodClass = (mood = '') => {
  if (['happy', 'excited', 'very_happy'].includes(mood)) return 'bg-green-100 text-green-700';
  if (mood === 'calm') return 'bg-blue-100 text-blue-700';
  if (['curious', 'engaged'].includes(mood)) return 'bg-violet-100 text-violet-700';
  if (['upset', 'anxious', 'sad'].includes(mood)) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-700';
};

// ─── Report card (list item) ─────────────────────────────────────────────────
function ReportCard({ report, onView, onSubmit, submitting }) {
  const isSubmitted = report.status === 'submitted';
  const mood = report.metadata?.dominant_mood || 'neutral';
  const eng = report.metadata?.avg_engagement;
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
              <BarChart2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{report.child_name}</p>
              <p className="text-sm text-gray-500">{report.month_label} · {report.report_count} session{report.report_count !== 1 ? 's' : ''}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {report.date_range?.first} → {report.date_range?.last}
              </p>
            </div>
          </div>
          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${isSubmitted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isSubmitted ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
            {isSubmitted ? 'Submitted' : 'Draft'}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <span className={`px-2 py-1 rounded ${moodClass(mood)}`}>Mood: {mood}</span>
          {eng != null && (
            <span className={`px-2 py-1 rounded ${eng >= 60 ? 'bg-green-100 text-green-700' : eng >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
              Avg Engagement: {eng}/100
            </span>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="outline" onClick={() => onView(report)}>
            <FileText className="w-4 h-4 mr-1" /> View Report
          </Button>
          {!isSubmitted && (
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => onSubmit(report.id)}
              disabled={submitting === report.id}
            >
              {submitting === report.id
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</>
                : <><Send className="w-4 h-4 mr-1" /> Submit to Parent</>}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Generate modal ──────────────────────────────────────────────────────────
function GenerateModal({ available, onGenerate, onClose, generating }) {
  // Use array index as the stable key — avoids null child_id comparison bug
  const [childIndex, setChildIndex] = useState('');
  const [monthKey, setMonthKey]     = useState('');

  const selectedChild = childIndex !== '' ? available[Number(childIndex)] : null;
  const selectedMonth = selectedChild && monthKey
    ? selectedChild.months.find(m => m.month === monthKey) || null
    : null;

  const handleChildChange = (e) => {
    setChildIndex(e.target.value);
    setMonthKey('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Generate Monthly Report</h3>
            <p className="text-sm text-gray-500 mt-0.5">Pick a student and a month</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Step 1 — Student */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Student
          </label>
          <select
            value={childIndex}
            onChange={handleChildChange}
            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="">— Select a student —</option>
            {available.map((child, idx) => (
              <option key={idx} value={idx}>
                {child.child_name} ({child.months.length} month{child.months.length !== 1 ? 's' : ''} available)
              </option>
            ))}
          </select>
        </div>

        {/* Step 2 — Month (only shown once a student is chosen) */}
        {selectedChild && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Month
            </label>
            <select
              value={monthKey}
              onChange={e => setMonthKey(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">— Select a month —</option>
              {selectedChild.months.map(m => (
                <option key={m.month} value={m.month}>
                  {m.label} · {m.report_count} report{m.report_count !== 1 ? 's' : ''}
                  {m.generated === 'submitted' ? ' ✓ submitted' : m.generated ? ' (draft exists)' : ''}
                </option>
              ))}
            </select>

            {/* Preview pill for selected month */}
            {selectedMonth && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700">
                <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  <strong>{selectedChild.child_name}</strong> · {selectedMonth.label} · {selectedMonth.report_count} session{selectedMonth.report_count !== 1 ? 's' : ''}
                  {selectedMonth.generated === 'submitted' && ' · already submitted'}
                  {selectedMonth.generated && selectedMonth.generated !== 'submitted' && ' · draft exists, will regenerate'}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            disabled={!selectedChild || !selectedMonth || generating}
            onClick={() => onGenerate(selectedChild, selectedMonth)}
          >
            {generating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── PDF download ────────────────────────────────────────────────────────────
// Filename convention: ChildName_Month_Monthly_Insight.pdf
function monthlyPdfFilename(report) {
  const safeName = (report.child_name || 'child').replace(/\s+/g, '_');
  const safeMonth = (report.month_label || report.month || 'month').replace(/\s+/g, '_');
  return `${safeName}_${safeMonth}_Monthly_Insight.pdf`;
}

function downloadMonthlyPDF(report) {
  const rd = report.content || {};

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  // Text-first (v2) report — same free-text format the daily report uses
  if (rd.report_text) {
    renderTextReportPdf(doc, rd.report_text, { marginX: 48 });
    doc.save(monthlyPdfFilename(report));
    return;
  }

  const cri = rd.curiosity_response_index || {};
  const engagement = rd.engagement_score || {};
  const ogs = rd.overall_growth_score || {};

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const maxWidth = pageWidth - marginX * 2;

  const DARK_BLUE = [30, 58, 138];
  const GREEN     = [22, 163, 74];
  const YELLOW    = [234, 179, 8];
  const RED       = [220, 38, 38];
  const TEXT      = [30, 41, 59];
  const MUTED     = [100, 116, 139];

  let y = 56;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - 48) { doc.addPage(); y = 56; }
  };

  const ratingColor = (r) => {
    const k = (r || '').toLowerCase().replace(/\s+/g, '_');
    if (k === 'excellent')  return DARK_BLUE;
    if (k === 'good')       return GREEN;
    if (k === 'fair')       return YELLOW;
    if (k === 'needs_work') return RED;
    return MUTED;
  };
  const ratingLabel = (r) => {
    const k = (r || '').toLowerCase().replace(/\s+/g, '_');
    return ({ excellent: 'Excellent', good: 'Good', fair: 'Fair', needs_work: 'Needs Work' }[k]) || (r || '—');
  };
  const overallLabel = (r) => {
    const k = (r || '').toLowerCase();
    return ({ balanced: 'Balanced Growth', moderate: 'Moderate Growth', limited: 'Limited Growth' })[k] || (r || 'Pending');
  };
  const overallColor = (r) => {
    const k = (r || '').toLowerCase();
    if (k === 'balanced') return DARK_BLUE;
    if (k === 'moderate') return YELLOW;
    return RED;
  };

  const drawSquare = (x, yy, size, color) => {
    doc.setFillColor(color[0], color[1], color[2]);
    doc.rect(x, yy, size, size, 'F');
  };
  const sectionHeading = (text) => {
    ensureSpace(34);
    drawSquare(marginX, y - 9, 10, DARK_BLUE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...DARK_BLUE);
    doc.text(text, marginX + 18, y);
    y += 8;
    doc.setDrawColor(220, 226, 235);
    doc.setLineWidth(0.6);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 14;
  };
  const labelValue = (label, value) => {
    ensureSpace(18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...TEXT);
    const labelText = `${label}: `;
    doc.text(labelText, marginX + 4, y);
    const lw = doc.getTextWidth(labelText);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(String(value ?? '—'), maxWidth - lw - 4);
    doc.text(lines, marginX + 4 + lw, y);
    y += lines.length * 14 + 4;
  };
  const paragraph = (text, opts = {}) => {
    const txt = String(text ?? '—');
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setFontSize(opts.size || 11);
    doc.setTextColor(...(opts.color || TEXT));
    const lines = doc.splitTextToSize(txt, maxWidth - 4);
    lines.forEach((ln) => { ensureSpace(16); doc.text(ln, marginX + 4, y); y += 14; });
    y += 6;
  };

  // 1. Header
  sectionHeading("Month's Insights");
  labelValue("Child's Name", rd.child_name || report.child_name);
  labelValue('Period', rd.date || report.month_label);
  labelValue('Sessions', String(report.report_count || '—'));
  labelValue('Curiosity Seeds Explored', rd.curiosity_seed || '—');
  y += 4;

  // 2. Growth Metrics table
  sectionHeading('Growth Metrics & Observations');
  const rows = (rd.growth_metrics || []).map((m) => [m.area || '—', ratingLabel(m.rating), m.observation || '—']);
  if (rows.length === 0) rows.push(['—', '—', 'No growth metrics recorded']);
  autoTable(doc, {
    startY: y,
    head: [['Growth Area', 'Rating', 'Observation Summary']],
    body: rows,
    margin: { left: marginX, right: marginX },
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 6, textColor: TEXT, valign: 'top' },
    headStyles: { fillColor: DARK_BLUE, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 110, fontStyle: 'bold' },
      1: { cellWidth: 80, halign: 'center', fontStyle: 'bold' },
      2: { cellWidth: 'auto' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 1) {
        const c = ratingColor(data.cell.raw);
        data.cell.styles.fillColor = c;
        data.cell.styles.textColor = [255, 255, 255];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 18;

  // 3. Curiosity Response Index
  sectionHeading('Curiosity Response Index');
  ensureSpace(20);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...DARK_BLUE);
  doc.text(`Score: ${cri.score ?? '—'} / ${cri.max_score ?? 10}`, marginX + 4, y);
  y += 18;
  if (cri.analysis) paragraph(cri.analysis);

  // 4. Disposition + Engagement
  if (rd.disposition) {
    sectionHeading('Disposition During Sessions');
    if (engagement.score !== undefined) {
      ensureSpace(20);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...DARK_BLUE);
      doc.text(`Avg Engagement: ${engagement.score}/100${engagement.label ? ` · ${engagement.label}` : ''}`, marginX + 4, y);
      y += 16;
    }
    paragraph(rd.disposition);
  }

  // 5. Communication Skills
  if (rd.communication_skills) {
    sectionHeading('Communication Skills & Thought Clarity');
    paragraph(rd.communication_skills);
  }

  // 6. Overall Growth Score
  sectionHeading('Overall Growth Score');
  ensureSpace(28);
  const oc = overallColor(ogs.rating);
  drawSquare(marginX + 4, y - 10, 12, oc);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...oc);
  doc.text(`${overallLabel(ogs.rating)} — ${ogs.areas_active || 0}/${ogs.total_areas || 7} Areas Active`, marginX + 22, y);
  y += 18;
  if (ogs.summary) paragraph(ogs.summary);

  // 7. Note for Parent
  if (rd.note_for_parent) {
    sectionHeading('Note for Parent');
    paragraph(rd.note_for_parent);
  }

  // 8. Legend
  sectionHeading('Legend');
  const legendLine = (color, label, desc) => {
    ensureSpace(18);
    drawSquare(marginX + 4, y - 8, 9, color);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...TEXT);
    doc.text(label, marginX + 20, y);
    const lw = doc.getTextWidth(label);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
    doc.text(` — ${desc}`, marginX + 20 + lw, y);
    y += 16;
  };
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...TEXT);
  ensureSpace(18); doc.text('Performance by Area', marginX + 4, y); y += 14;
  legendLine(DARK_BLUE, 'Excellent',   'Clear growth with strong evidence');
  legendLine(GREEN,     'Good',        'Solid engagement with positive trends');
  legendLine(YELLOW,    'Fair',        'Some engagement, needs encouragement');
  legendLine(RED,       'Needs Work',  'Area not activated or underperforming');
  y += 4;
  ensureSpace(18); doc.setFont('helvetica', 'bold'); doc.text('Overall Growth Score', marginX + 4, y); y += 14;
  legendLine(DARK_BLUE, 'Balanced Growth', '6-7 areas active');
  legendLine(YELLOW,    'Moderate Growth', '4-5 areas active');
  legendLine(RED,       'Limited Growth',  '1-3 areas active');

  doc.save(monthlyPdfFilename(report));
}

// ─── Report viewer ───────────────────────────────────────────────────────────
function ReportViewer({ report, onClose, onSubmit, submitting }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-5 flex items-start justify-between flex-shrink-0">
          <div>
            <div className="text-xs opacity-75 uppercase tracking-wider mb-1">Month's Insights</div>
            <h2 className="text-xl font-bold">{report.child_name}</h2>
            <p className="text-sm text-purple-100 mt-0.5">
              {report.month_label} · {report.report_count} session{report.report_count !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {report.content && (
              <Button
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
                onClick={() => downloadMonthlyPDF(report)}
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
              </Button>
            )}
            {report.status !== 'submitted' && (
              <Button
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
                onClick={() => onSubmit(report.id)}
                disabled={submitting === report.id}
              >
                {submitting === report.id
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Submitting…</>
                  : <><Send className="w-3.5 h-3.5 mr-1" /> Submit to Parent</>}
              </Button>
            )}
            {report.status === 'submitted' && (
              <span className="flex items-center gap-1 text-xs bg-green-400/20 text-white px-3 py-1.5 rounded-full border border-green-300/40">
                <CheckCircle className="w-3.5 h-3.5" /> Submitted to Parent
              </span>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white ml-2">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {report.content
            ? (
              <>
                <DaysInsightsReport data={report.content} title="Month's Insights" />
                <MonthlyInsightExtras data={report.content} />
              </>
            )
            : (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ObserverMonthlyReports() {
  const { toast } = useToast();
  const token = localStorage.getItem('observer_token');

  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [available, setAvailable] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, availRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/monthly-reports?token=${token}`),
        axios.get(`${BACKEND_URL}/api/observer/monthly-reports/available?token=${token}`),
      ]);
      setReports(listRes.data.reports || []);
      setAvailable(availRes.data.children || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load monthly reports', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { load(); }, [load]);

  // Always fetch the full report (with content) before viewing
  const handleView = useCallback(async (report) => {
    if (report.content) {
      setViewing(report);
      return;
    }
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/observer/monthly-reports/${report.id}?token=${token}`
      );
      setViewing(res.data.report);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load report', variant: 'destructive' });
    }
  }, [token, toast]);

  const handleGenerate = async (child, month) => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/observer/monthly-reports/generate?token=${token}`,
        {
          // child_id may be null in legacy data — fall back to child_name so
          // the backend's name-based fallback query can pick it up
          child_id: child.child_id || child.child_name,
          child_name: child.child_name,
          month: month.month,
        }
      );
      toast({ title: 'Report generated', description: `${child.child_name} – ${month.label}` });
      setShowGenerate(false);
      await load();
      setViewing(res.data.report);
    } catch (e) {
      // FastAPI validation errors return detail as an array of objects — never
      // pass that array directly to React children or it crashes the renderer.
      const raw = e?.response?.data?.detail;
      const detail =
        typeof raw === 'string'
          ? raw
          : Array.isArray(raw)
          ? raw.map(d => d.msg || JSON.stringify(d)).join('; ')
          : 'Generation failed — please try again.';
      toast({ title: 'Generation failed', description: detail, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (reportId) => {
    setSubmitting(reportId);
    try {
      await axios.post(`${BACKEND_URL}/api/observer/monthly-reports/${reportId}/submit?token=${token}`);
      toast({ title: 'Submitted to parent', description: 'The monthly report is now visible on the parent dashboard.' });
      await load();
      if (viewing?.id === reportId) {
        setViewing(prev => ({ ...prev, status: 'submitted' }));
      }
    } catch (e) {
      toast({ title: 'Submit failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Monthly Reports</h2>
          <p className="text-sm text-gray-500">Aggregated Month's Insights per student</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => setShowGenerate(true)}
            disabled={available.length === 0}
          >
            <Sparkles className="w-4 h-4 mr-1" /> Generate New
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {reports.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-14 text-center">
            <BarChart2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No monthly reports yet</p>
            <p className="text-gray-400 text-sm mt-1">Generate your first report to get started</p>
            {available.length > 0 ? (
              <Button
                className="mt-5 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => setShowGenerate(true)}
              >
                <Sparkles className="w-4 h-4 mr-2" /> Generate First Report
              </Button>
            ) : (
              <p className="text-gray-400 text-xs mt-3">Submit daily session reports first to enable monthly generation.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reports list */}
      <div className="space-y-4">
        {reports.map(r => (
          <ReportCard
            key={r.id}
            report={r}
            onView={handleView}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        ))}
      </div>

      {showGenerate && (
        <GenerateModal
          available={available}
          onGenerate={handleGenerate}
          onClose={() => setShowGenerate(false)}
          generating={generating}
        />
      )}

      {viewing && (
        <ReportViewer
          report={viewing}
          onClose={() => setViewing(null)}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}
    </div>
  );
}
